/**
 * Event hooks for the organizer side (`/me/*`).
 *
 * Mutations invalidate exactly the keys they can affect: the list views, the
 * affected event, and its rounds.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  CreateEventInput,
  EventListResponse,
  EventMutationResponse,
  RunRecommendationResponse,
  RoundDetailResponse,
  RoundsResponse,
  UpdateEventInput,
  WeatherSuggestionResponse,
} from '@shared/types'
import type { Locale } from '@shared/locales'

import { apiRequest } from '../lib/api'
import { queryKeys } from '../lib/query'
import { selectIsAuthenticated, useSessionStore } from '../stores/session'

export function useEventList() {
  const isAuthenticated = useSessionStore(selectIsAuthenticated)

  return useQuery({
    queryKey: queryKeys.events,
    enabled: isAuthenticated,
    queryFn: () => apiRequest<EventListResponse>('/api/events'),
  })
}

export function useEventDetail(eventId: string | undefined) {
  const isAuthenticated = useSessionStore(selectIsAuthenticated)

  return useQuery({
    queryKey: queryKeys.event(eventId ?? ''),
    enabled: isAuthenticated && Boolean(eventId),
    queryFn: () => apiRequest<EventMutationResponse>(`/api/events/${eventId}`),
  })
}

export function useCreateEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateEventInput) =>
      apiRequest<EventMutationResponse>('/api/events', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.events })
    },
  })
}

export function useUpdateEvent(eventId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: UpdateEventInput) =>
      apiRequest<EventMutationResponse>(`/api/events/${eventId}`, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.event(eventId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.events })
    },
  })
}

export function useArchiveEvent(eventId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      apiRequest<EventMutationResponse>(`/api/events/${eventId}/archive`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.event(eventId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.events })
    },
  })
}

export function useEventRounds(eventId: string | undefined) {
  const isAuthenticated = useSessionStore(selectIsAuthenticated)

  return useQuery({
    queryKey: queryKeys.eventRounds(eventId ?? ''),
    enabled: isAuthenticated && Boolean(eventId),
    queryFn: () => apiRequest<RoundsResponse>(`/api/events/${eventId}/recommendations`),
  })
}

/**
 * Starts a round.
 *
 * `background` asks the server for the row now and the result later, which is
 * what the interface sends once it knows this event's rounds are slow: the
 * request then returns a queued round the page can poll instead of holding a
 * connection open for the whole pipeline. `relaxedTravelMinutes` carries the
 * relaxation only when the organizer accepted the suggestion a failed round gave
 * them.
 */
export function useRunRecommendation(eventId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: { relaxedTravelMinutes?: number; background?: boolean } = {}) =>
      apiRequest<RunRecommendationResponse>(`/api/events/${eventId}/recommendations`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.eventRounds(eventId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.event(eventId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.events })
    },
  })
}

export function useDecideRound(eventId: string, round: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (recommendationId: string) =>
      apiRequest<RoundDetailResponse>(`/api/events/${eventId}/rounds/${round}/decide`, {
        method: 'POST',
        body: { recommendationId },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.eventRounds(eventId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.event(eventId) })
      // The share-link pages cache the same round under their own keys, and the
      // decision is visible there too.
      void queryClient.invalidateQueries({ queryKey: queryKeys.shared })
    },
  })
}

export function useWeatherSuggestion(city: string, date: string, locale: Locale) {
  const isAuthenticated = useSessionStore(selectIsAuthenticated)

  return useQuery({
    queryKey: queryKeys.weather(city, date),
    enabled: isAuthenticated && Boolean(date),
    staleTime: 10 * 60 * 1000,
    queryFn: () =>
      apiRequest<WeatherSuggestionResponse>(
        `/api/weather?city=${encodeURIComponent(city)}&date=${encodeURIComponent(date)}`,
        { locale },
      ),
  })
}
